import React from "react";

import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import Link from "next/link";

// SVG imports

//-------------SVG----------------
import AngellistSvg from "@/assets/svg/socialIcon/angellist.svg";
import CbInsightsSvg from "@/assets/svg/socialIcon/cbinsights.svg";
import CraftCoSvg from "@/assets/svg/socialIcon/craftco.svg";
import CrunchbaseSvg from "@/assets/svg/socialIcon/crunchbase.svg";
import DatanyzeSvg from "@/assets/svg/socialIcon/datanyze.svg";
import DealroomSvg from "@/assets/svg/socialIcon/dealroom.svg";
import DunBradstreetSvg from "@/assets/svg/socialIcon/dunbradstreet.svg";
import FacebookSvg from "@/assets/svg/socialIcon/facebook.svg";
import FundcombSvg from "@/assets/svg/socialIcon/fundcomb.svg";
import GrowjoSvg from "@/assets/svg/socialIcon/growjo.svg";
import GsenseSvg from "@/assets/svg/socialIcon/gsense.svg";
import InstagramSvg from "@/assets/svg/socialIcon/instagram.svg";
import LinkedinSvg from "@/assets/svg/socialIcon/linkedin.svg";
import NfxSignalSvg from "@/assets/svg/socialIcon/nfxsignal.svg";
import OpencorporatesSvg from "@/assets/svg/socialIcon/opencorporates.svg";
import OpenvcSvg from "@/assets/svg/socialIcon/openvc.svg";
import OwlerSvg from "@/assets/svg/socialIcon/owler.svg";
import PitchbookSvg from "@/assets/svg/socialIcon/pitchbook.svg";
import PreqinSvg from "@/assets/svg/socialIcon/preqin.svg";
import PrivcoSvg from "@/assets/svg/socialIcon/privco.svg";
import RocketreacSvg from "@/assets/svg/socialIcon/rocketreac.svg";
import StateCorpSosSvg from "@/assets/svg/socialIcon/statecorpsos.svg";
import TheorgSvg from "@/assets/svg/socialIcon/theorg.svg";
import TracxnSvg from "@/assets/svg/socialIcon/tracxn.svg";
import TwitterSvg from "@/assets/svg/socialIcon/twitter.svg";
import UnicornNestSvg from "@/assets/svg/socialIcon/unicornnest.svg";
import WebSiteSvg from "@/assets/svg/socialIcon/website.svg";
import ZoominfoSvg from "@/assets/svg/socialIcon/zoominfo.svg";

import type { ExternalUrlData } from "./ExternalUrlIcons.client";

/**
 * Map of URL types to their corresponding SVG components
 * This is the canonical source of truth for URL icon mapping
 */
const externalUrlIconsMap: Record<string, React.ComponentType> = {
  urlWebsite: WebSiteSvg,
  urlLinkedin: LinkedinSvg,
  urlAngellist: AngellistSvg,
  urlCbInsights: CbInsightsSvg,
  urlCraftСo: CraftCoSvg,
  urlCrunchbase: CrunchbaseSvg,
  urlDatanyze: DatanyzeSvg,
  urlDealroom: DealroomSvg,
  urlDunBradstreet: DunBradstreetSvg,
  urlFacebook: FacebookSvg,
  urlFundcomb: FundcombSvg,
  urlGsense: GsenseSvg,
  urlGrowjo: GrowjoSvg,
  urlInstagram: InstagramSvg,
  urlNfxSignal: NfxSignalSvg,
  urlOpencorporates: OpencorporatesSvg,
  urlOpenvc: OpenvcSvg,
  urlOwler: OwlerSvg,
  urlPitchbook: PitchbookSvg,
  urlPreqin: PreqinSvg,
  urlPrivco: PrivcoSvg,
  urlRocketreac: RocketreacSvg,
  urlStateCorpSos: StateCorpSosSvg,
  urlTheorg: TheorgSvg,
  urlTracxn: TracxnSvg,
  urlTwitter: TwitterSvg,
  urlUnicornNest: UnicornNestSvg,
  urlZoominfo: ZoominfoSvg,
};

/**
 * URL object from server-side data structure
 */
interface UrlObject {
  urlType: string;
  url: string;
  isCurrent?: number;
  isPrimary?: number;
}

/**
 * Props for ExternalUrlIcons server component
 */
interface ExternalUrlIconsServerProps {
  /**
   * Data containing URL information. Can be in multiple formats:
   * - Object with ExternalUrlData properties
   * - Object with a webUrl property containing ExternalUrlData
   * - Array of UrlObject for specialized server-side rendering
   */
  data: ExternalUrlData | { webUrl: ExternalUrlData } | UrlObject[];
  /**
   * Optional custom styles to apply to the container
   */
  customStyle?: Record<string, unknown>;
  /**
   * Optional custom styles to apply to the icons
   */
  customStyleIcon?: Record<string, unknown>;
  /**
   * Type of display context, affects rendering behavior
   */
  type?: string;
  /**
   * Page type, used for determining data processing method
   */
  pageType?: string;
}

/**
 * Internal representation of a URL icon
 */
interface UrlIcon {
  icon: React.ComponentType;
  url: string;
  name: string;
}

/**
 * Ensures a URL has a protocol prefix
 * 
 * @param url - The URL to check and possibly modify
 * @returns URL with proper protocol prefix
 */
export function urlChecker(url: string | undefined): string {
  if (!url) {
    return "";
  }
  if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
    return url;
  }
  return "https://" + url;
}

/**
 * ExternalUrlIcons - Server-side component for rendering icons that link to external URLs
 * 
 * This component displays a series of icons that link to various external
 * services like LinkedIn, Twitter, Crunchbase, etc. based on the URL data provided.
 * 
 * @see src/components/ui/buttons/ExternalUrlIcons.client.tsx - Client-side counterpart component
 */
export const ExternalUrlIcons = ({
  data: innerData,
  customStyle,
  customStyleIcon,
  type,
  pageType,
}: ExternalUrlIconsServerProps) => {
  // Debug the incoming data
  console.log('ExternalUrlIcons.server - innerData:', innerData);
  console.log('ExternalUrlIcons.server - pageType:', pageType);
  console.log('ExternalUrlIcons.server - type:', type);
  
  // Handle different data formats
  const data = 'webUrl' in innerData && innerData.webUrl 
    ? innerData.webUrl 
    : innerData;
  
  console.log('ExternalUrlIcons.server - processed data:', data);

  /**
   * Filters objects by urlType, taking into account the fields isCurrent, isPrimary
   * 
   * @param urls - Array of URL objects to filter
   * @returns Filtered array of unique URL objects
   */
  const filterUniqueUrls = (urls: UrlObject[] | undefined): UrlObject[] => {
    if (!Array.isArray(urls)) return [];
    
    const urlMap = new Map<string, UrlObject>();

    urls.forEach((urlObj) => {
      if (!urlObj) return;
      const { urlType, isCurrent, isPrimary } = urlObj;
      if (!urlType) return;

      if (!urlMap.has(urlType)) {
        urlMap.set(urlType, urlObj);
      } else {
        const existingObj = urlMap.get(urlType);
        if (isCurrent === 1 && isPrimary === 1) {
          if (existingObj && (existingObj.isCurrent !== 1 || existingObj.isPrimary !== 1)) {
            urlMap.set(urlType, urlObj);
          }
        }
      }
    });

    return Array.from(urlMap.values());
  };

  /**
   * Processes data to generate a list of URL icons based on the provided data
   * 
   * @returns Array of UrlIcon objects or undefined if no valid URLs found
   */
  const getExternalUrlIcons = (): UrlIcon[] | undefined => {
    // Helper function to get icons from object data
    const getIconsFromObjectData = (objData: Record<string, unknown>): UrlIcon[] | undefined => {
      console.log('ExternalUrlIcons.server - object data branch');
      console.log('ExternalUrlIcons.server - data keys:', Object.keys(objData));
      
      const keysArr = Object.keys(externalUrlIconsMap).filter((key) => {
        const hasKey = key.startsWith("url") && key in objData;
        const hasValue = hasKey && objData[key] !== "";
        console.log(`ExternalUrlIcons.server - checking key ${key}: hasKey=${hasKey}, hasValue=${hasValue}`);
        return hasKey && hasValue;
      });

      console.log('ExternalUrlIcons.server - filtered keys:', keysArr);
      if (keysArr.length === 0) return undefined;

      return keysArr.map((key) => ({
        icon: externalUrlIconsMap[key],
        url: objData[key] as string,
        name: key.slice(3),
      })).filter((item): item is UrlIcon => 
        // Filter out items with missing icon or URL
        !!item.icon && !!item.url
      );
    };
    
    // Special handling for companiesList pageType
    if (pageType === 'companiesList') {
      console.log('ExternalUrlIcons.server - companiesList branch, data:', data);
      
      // For companiesList, we expect data to be an object with properties like urlWebsite, etc.
      if (!data || typeof data !== 'object') return undefined;
      
      // Transform the data to ensure it has the expected format
      const transformedData: Record<string, string> = {};
      
      // Handle the case where data has a urlWebsite property directly
      if ('urlWebsite' in data && data.urlWebsite) {
        transformedData.urlWebsite = data.urlWebsite as string;
      }
      
      // Handle the case where data has a links property with urlWebsite
      if ('links' in data && typeof data.links === 'object' && data.links) {
        Object.entries(data.links as Record<string, unknown>).forEach(([key, value]) => {
          // Ensure keys start with 'url'
          const urlKey = key.startsWith('url') ? key : `url${key.charAt(0).toUpperCase() + key.slice(1)}`;
          if (value && typeof value === 'string') {
            transformedData[urlKey] = value;
          }
        });
      }
      
      // Handle the case where data is a company object with a links property
      if ('company' in data && typeof data === 'object') {
        // This is a company item object, extract website from links
        if (data.links && typeof data.links === 'object') {
          Object.entries(data.links as Record<string, unknown>).forEach(([key, value]) => {
            // Ensure keys start with 'url'
            const urlKey = key.startsWith('url') ? key : `url${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (value && typeof value === 'string') {
              transformedData[urlKey] = value;
            }
          });
        }
      }
      
      console.log('ExternalUrlIcons.server - transformedData:', transformedData);
      
      // Use the standard object data branch with the transformed data
      return getIconsFromObjectData(transformedData);
    }
    
    // Original array data branch
    if (pageType && Array.isArray(data)) {
      console.log('ExternalUrlIcons.server - pageType array branch, data:', data);
      
      const makeKey = (str: string): string => {
        const capitalizeStr =
          str.substring(0, 1).toUpperCase() + str.substring(1);
        return "url" + capitalizeStr;
      };

      const filteredData = filterUniqueUrls(data as UrlObject[]);

      // Create icons from filtered URL objects
      const icons = filteredData?.map((item) => {
        const key = makeKey(item?.urlType);
        return {
          name: item?.urlType,
          url: item?.url,
          icon: externalUrlIconsMap[key],
        };
      }).filter((item): item is UrlIcon => 
        // Filter out items with missing icon or URL
        !!item.icon && !!item.url
      );

      return icons.length > 0 ? icons : undefined;
    }
    
    
    // Standard object data branch
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return getIconsFromObjectData(data as Record<string, unknown>);
    }
    
    return undefined;
  };

  const externalUrlIcons = getExternalUrlIcons();
  console.log('ExternalUrlIcons.server - final icons:', externalUrlIcons);
  
  /**
   * Default flex styles for the icon container
   * Ensures proper wrapping and spacing of icons
   * 
   * @see {@link src/components/companies/companiesList/companiesListComponents/tableMain/TableElements.tsx}
   */
  const styleFlex = {
    gap: "12px",
    w: "100%",
    justify: { base: "center", smx: "left" },
    flexWrap: "wrap" as const, // Type assertion to avoid ResponsiveValue<FlexWrap> error
    // Apply custom style from props, but ensure flexWrap is always enabled for the Links column
    ...(type === "links" ? {
      flexWrap: "wrap" as const,
      minWidth: "200px",
      maxWidth: "200px",
      overflow: "visible",
      ...customStyle,
      // Override any custom style that might disable wrapping
      flexDir: "row",
    } : customStyle),
  };

  // Generate link props for a URL icon
  const getLinkProps = (item: UrlIcon) => {
    return {
      href: urlChecker(item.url),
      target: "_blank",
      rel: "noopener noreferrer",
      "aria-label": item?.name,
    };
  };

  let content = null;

  // Safe check for externalUrlIcons existence and non-empty array
  /**
   * Render the external URL icons if available
   * Special handling for links column to ensure proper wrapping
   * 
   * @see {@link src/components/companies/companiesList/companiesListComponents/tableMain/TableElements.tsx}
   */
  if (externalUrlIcons && externalUrlIcons.length > 0) {
    content = (
      <Flex {...styleFlex}>
        {externalUrlIcons.map((item) => {
          return (
            <Box 
              key={`externalUrl_${item.name}_${item.url}`} 
              cursor={"pointer"}
              // Ensure icons have proper spacing and don't overflow
              display="inline-flex"
              m="2px"
            >
              <Link {...getLinkProps(item)}>
                <Icon
                  as={item.icon}
                  w={type === "links" ? "20px" : "24px"}
                  h={type === "links" ? "20px" : "24px"}
                  // Apply custom icon style but ensure proper sizing for links column
                  {...(type === "links" ? {
                    ...customStyleIcon,
                    minWidth: "20px",
                    minHeight: "20px",
                  } : customStyleIcon)}
                />
              </Link>
            </Box>
          );
        })}
      </Flex>
    );
  } else if (type === "companiesList") {
    content = <Text textAlign="center">—</Text>;
  }

  return <>{content}</>;
};

// For backward compatibility
export const SocialBlockServer = ExternalUrlIcons;
